import { useEffect, useRef } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import './RichTextEditor.css';

interface RichTextEditorProps {
  // HTML value (stored as HTML, like the legacy Quill editors).
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
}

// Shared Quill (snow theme) rich-text editor — the React equivalent of the legacy
// Quill editors (deployment notices, FAQ answers). Value is HTML in/out.
export function RichTextEditor({ value, onChange, placeholder, className, style }: RichTextEditorProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const quillRef = useRef<Quill | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!hostRef.current || quillRef.current) return;
    const q = new Quill(hostRef.current, {
      theme: 'snow',
      placeholder,
      modules: {
        toolbar: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ color: [] }, { background: [] }],
          ['link'],
          ['clean'],
        ],
      },
    });
    quillRef.current = q;
    if (value) q.clipboard.dangerouslyPasteHTML(value);
    q.on('text-change', () => {
      const html = q.getSemanticHTML ? q.getSemanticHTML() : q.root.innerHTML;
      onChangeRef.current(html === '<p></p>' ? '' : html);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync external value changes (e.g. switching the edited item) without clobbering typing.
  useEffect(() => {
    const q = quillRef.current;
    if (!q) return;
    const current = q.root.innerHTML;
    const next = value || '';
    if (next !== current && (next || current !== '<p></p>')) {
      q.clipboard.dangerouslyPasteHTML(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <div className={`fk-rte${className ? ` ${className}` : ''}`} style={style}>
      <div ref={hostRef} style={{ backgroundColor: 'var(--custom-white, #fff)' }} />
    </div>
  );
}
